import { format, parse } from 'date-fns';

export const formatMonthDayYear = (year: string, month: string, day: string): string => {
  return `${month}/${day}/${year}`;
};

/**
 * Identifies ISO 8601 date-time strings in text and formats them using a provided formatting function
 * if no match is found for the iso date-time provided it returns the original string without formatting
 *
 * @param {string} text - The input text that may contain ISO date-time strings
 * @param {Function} formatFn - A function that takes year, month, and day strings and returns a formatted date string
 * @returns {string} The input text with any ISO date-time strings replaced with formatted dates
 *
 * @example
 * // Returns "Date: 01/15/2023"
 * formatIsoDateTimes("Date: 2023-01-15T12:00:00Z", (year, month, day) => `${month}/${day}/${year}`);
 */
export const formatIsoDateTimes = (text: string, formatFn: (year: string, month: string, day: string) => string): string => {
  // match ISO 8601 full date-time strings
  const isoDateTimeRegex = /\b(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?\b/g;

  // If no match, returns original string not formatted
  if (!isoDateTimeRegex.test(text)) {
    return text;
  }

  // return formatted string
  return text.replace(isoDateTimeRegex, (_match, year, month, day) => {
    return formatFn(year, month, day);
  });
};

export const dateFormatter = (date: string, formatType: string) => {
  return format(parse(date, formatType, new Date()), 'LLL d, yyyy').toUpperCase();
};

export const getUTCDate = (dateInput: Date | string | number) => {
  const date = new Date(dateInput);
  const cleanedDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return format(cleanedDate, 'MMM d, yyyy');
};

export const getBrowserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const convertDateToTimezone = (date: Date, timezone: string) => {
  const dateInUTC = new Date(date);
  const dateInTimezone = new Date(dateInUTC.toLocaleString('en-US', { timeZone: timezone }));
  return dateInTimezone;
};

export const getTimezoneDate = (date: Date) => {
  const timezone = getBrowserTimezone();
  return convertDateToTimezone(date, timezone);
};

export const isValidDate = (d: unknown) => {
  return d instanceof Date && !isNaN(d as unknown as number);
};
